import React from "react";
import "./Steps.css";
import { supabase } from "../../utils/supabaseClient";
import { useAuth } from "../../contexts/AuthContext";
import ZilchTooltip from "../ZilchToolTip/ZilchTooltip";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// `lca-documents` is private, so the stored value is a storage *path* and the
// viewable link is built here, at render time. Deliberately not persisted: this
// origin is http://localhost:8000 on a dev machine, and baking that into the
// column is how five localhost links ended up in production data.
// Absolute values are legacy rows written before that change.
function emissionsDocHref(value) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_BASE_URL}/cert-files?path=${encodeURIComponent(value)}`;
}

// Where a prefilled value actually came from. Only claim a source we can point
// at: the catalogue row, or a figure parsed out of the product's own details
// text. A value the user typed gets no badge — telling someone we "detected"
// what they just entered is the one thing a carbon-accounting form must not do.
const SOURCE_LABEL = {
  catalogue: "From your product catalogue",
  detected: "Detected from the product description",
};

function Step1ProductInfo({ formData, fieldSources = {}, updateFormData, validationErrors = [], isLoading = false }) {
  const { supplierInfo } = useAuth();
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);

  // Most people can't weigh a single pen, but the carton on the shelf has its
  // gross weight and unit count printed on it. Hidden by default so the common
  // case stays a single field.
  const [showCartonCalc, setShowCartonCalc] = React.useState(false);
  const [cartonWeight, setCartonWeight] = React.useState("");
  const [cartonQty, setCartonQty] = React.useState("");

  const cartonUnitGrams = (kg, qty) => {
    const w = parseFloat(kg);
    const q = parseFloat(qty);
    if (!(w > 0) || !(q > 0)) return null;
    return Math.round(((w * 1000) / q) * 100) / 100;
  };

  // Only ever writes a complete calculation, so half-typed carton figures never
  // wipe a weight the user already has.
  const applyCarton = (kg, qty) => {
    const grams = cartonUnitGrams(kg, qty);
    if (grams !== null) updateFormData("productInfo", "weight", grams);
  };

  const derivedUnitGrams = cartonUnitGrams(cartonWeight, cartonQty);

  // Set default product type to "purchased" if not set
  React.useEffect(() => {
    if (!formData.productInfo.productType) {
      updateFormData("productInfo", "productType", "purchased");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to check if a field has an error
  const hasError = (fieldKeyword) => {
    return validationErrors.some(err => err.toLowerCase().includes(fieldKeyword.toLowerCase()));
  };

  // Helper to check if a field is filled
  const isFilled = (value) => {
    return value !== "" && value !== null && value !== undefined;
  };

  // Handle file upload to Supabase Storage
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Get supplier_id from auth context
    const supplierId = supplierInfo?.supplier_id;
    if (!supplierId) {
      setUploadError('User supplier information not available');
      return;
    }

    setUploadingFile(true);
    setUploadError(null);

    try {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `supplier-emissions-${timestamp}.${fileExt}`;
      // IMPORTANT: Include supplier_id in the path for security and organization
      const filePath = `${supplierId}/${fileName}`;

      // Upload file to Supabase Storage
      const { error } = await supabase.storage
        .from('lca-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Store the storage path, not a URL — the bucket is private and the
      // viewable link is built at render time by emissionsDocHref().
      updateFormData("productInfo", "supplierEmissionsFileName", file.name);
      updateFormData("productInfo", "supplierEmissionsFileUrl", filePath);

    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="step-container" style={{ position: 'relative' }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(4px)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          borderRadius: '16px'
        }}>
          {/* Spinner */}
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#06a457',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}></div>
          
          {/* Loading Text */}
          <div style={{
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#0f172a',
              marginBottom: '4px'
            }}>
              Loading product data...
            </div>
            <div style={{
              fontSize: '14px',
              color: '#64748b'
            }}>
              Please wait while we fetch your product information
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {/* Header */}
      <div className="step-header">
        <div className="step-number">01</div>
        <div>
          <h2 className="step-title">Product Information</h2>
          <p className="step-subtitle">Let's start with the basics about your product</p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="form-section">
        {/* Row 1: Product Name & SKU */}
        <div className="form-row">
          <div className="form-field">
            <label className="field-label">
              Product Name
              <span className="field-required">*</span>
              <ZilchTooltip copy="Enter the full name of your product as it appears on your store or catalog" />
            </label>
            <input
              type="text"
              className={`field-input ${hasError("product name") ? "field-input--error" : ""} ${isFilled(formData.productInfo.name) ? "field-input--filled" : ""}`}
              value={formData.productInfo.name}
              onChange={(e) => updateFormData("productInfo", "name", e.target.value)}
              placeholder="e.g., Organic Cotton T-Shirt"
              disabled={isLoading}
            />
            {hasError("product name") && (
              <div className="field-error">Product name is required</div>
            )}
          </div>

          <div className="form-field">
            <label className="field-label">
              SKU / Product Code
              <span className="field-required">*</span>
            </label>
            <input
              type="text"
              className={`field-input ${hasError("sku") ? "field-input--error" : ""} ${isFilled(formData.productInfo.sku) ? "field-input--filled" : ""}`}
              value={formData.productInfo.sku}
              onChange={(e) => updateFormData("productInfo", "sku", e.target.value)}
              placeholder="e.g., TSH-001"
              disabled={isLoading}
            />
            {SOURCE_LABEL[fieldSources.sku] && !hasError("sku") && (
              <div className="field-hint">{SOURCE_LABEL[fieldSources.sku]}</div>
            )}
            {hasError("sku") && (
              <div className="field-error">SKU/Product Code is required</div>
            )}
          </div>
        </div>

        {/* Row 2: Weight */}
        <div className="form-row">
          <div className="form-field">
            <label className="field-label">
              Weight (grams)
              <span className="field-required">*</span>
            </label>
            <input
              type="number"
              className={`field-input ${hasError("weight") ? "field-input--error" : ""} ${isFilled(formData.productInfo.weight) ? "field-input--filled" : ""}`}
              value={formData.productInfo.weight === "" ? "" : formData.productInfo.weight}
              onChange={(e) => {
                const v = e.target.value;
                const grams = v === "" ? "" : Math.max(0, parseFloat(v));
                updateFormData("productInfo", "weight", grams);
              }}
              placeholder="e.g., 150"
              disabled={isLoading}
            />
            {hasError("weight") && (
              <div className="field-error">Weight must be greater than 0</div>
            )}
            {SOURCE_LABEL[fieldSources.weight] && !hasError("weight") && (
              <div className="field-hint">{SOURCE_LABEL[fieldSources.weight]}</div>
            )}
            {/* Sanity nudge, independent of provenance — a prefilled weight over
                a kilo is just as worth a second look as a typed one. */}
            {formData.productInfo.weight > 1000 && !hasError("weight") && (
              <div className="field-hint text-warning">
                That's over 1&nbsp;kg — check this is the weight of a single unit, not a carton.
              </div>
            )}

            <button
              type="button"
              className="carton-calc-toggle"
              onClick={() => setShowCartonCalc(!showCartonCalc)}
              disabled={isLoading}
            >
              {showCartonCalc ? "Hide carton calculator" : "Don't know the unit weight? Calculate it from a carton"}
            </button>

            {showCartonCalc && (
              <div className="carton-calc">
                <div className="carton-calc__row">
                  <div className="carton-calc__field">
                    <label className="field-label">Carton weight (kg)</label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="field-input"
                      value={cartonWeight}
                      onChange={(e) => {
                        setCartonWeight(e.target.value);
                        applyCarton(e.target.value, cartonQty);
                      }}
                      placeholder="e.g., 1"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="carton-calc__field">
                    <label className="field-label">Units per carton</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="field-input"
                      value={cartonQty}
                      onChange={(e) => {
                        setCartonQty(e.target.value);
                        applyCarton(cartonWeight, e.target.value);
                      }}
                      placeholder="e.g., 10"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {derivedUnitGrams !== null ? (
                  <div className="field-hint">
                    {cartonWeight}&nbsp;kg ÷ {cartonQty} units = <strong>&nbsp;{derivedUnitGrams}&nbsp;g</strong>&nbsp;per unit, filled in above.
                  </div>
                ) : (
                  <div className="carton-calc__note">
                    Enter both figures and we'll fill in the unit weight for you. Use the gross carton weight if that's all you have — it slightly over-states the product, which is the safer direction.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="form-divider"></div>

        {/* Product Type Section */}
        <div className="form-field">
          <label className="field-label">
            How is this product sourced?
            <span className="field-required">*</span>
          </label>
          <div className="d-flex gap-2 justify-content-between align-items-stretch" style={{ flexWrap: 'wrap' }}>
            <label className={`radio-card ${formData.productInfo.productType === "purchased" ? "radio-card--selected" : ""}`}>
              <input
                type="radio"
                name="productType"
                value="purchased"
                checked={formData.productInfo.productType === "purchased"}
                onChange={(e) => updateFormData("productInfo", "productType", e.target.value)}
                className="radio-input"
                disabled={isLoading}
              />
              <div className="radio-content">
                <div className="radio-title">Purchased / Imported</div>
                <div className="radio-description">We buy this product from suppliers</div>
              </div>
              <div className="radio-check">
                {formData.productInfo.productType === "purchased" && (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#06a457"/>
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </label>

            <label className={`radio-card ${formData.productInfo.productType === "manufactured" ? "radio-card--selected" : ""}`}>
              <input
                type="radio"
                name="productType"
                value="manufactured"
                checked={formData.productInfo.productType === "manufactured"}
                onChange={(e) => {
                  updateFormData("productInfo", "productType", e.target.value);
                  // Clear purchased-only fields so a stale "yes, supplier provides data"
                  // flag can't cause the wizard to skip the Materials step.
                  updateFormData("productInfo", "supplierDataAvailable", null);
                  updateFormData("productInfo", "supplierEmissions", "");
                  updateFormData("productInfo", "supplierEmissionsFileName", "");
                  updateFormData("productInfo", "supplierEmissionsFileUrl", "");
                }}
                className="radio-input"
                disabled={isLoading}
              />
              <div className="radio-content">
                <div className="radio-title">Manufactured by us</div>
                <div className="radio-description">We produce this product in-house</div>
              </div>
              <div className="radio-check">
                {formData.productInfo.productType === "manufactured" && (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#06a457"/>
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Conditional: Supplier Data */}
        {formData.productInfo.productType === "purchased" && (
          <div className="conditional-section">
            <div className="form-field">
              <label className="field-label">
                Do your suppliers provide emissions data?
                <span className="field-required">*</span>
              </label>
              <div className="d-flex gap-2 justify-content-between align-items-stretch" style={{ flexWrap: 'wrap' }}>
                <label className={`radio-pill ${formData.productInfo.supplierDataAvailable === true ? "radio-pill--selected" : ""}`}>
                  <input
                    type="radio"
                    name="supplierDataAvailable"
                    value="yes"
                    checked={!!formData.productInfo.supplierDataAvailable}
                    onChange={() => updateFormData("productInfo", "supplierDataAvailable", true)}
                    className="radio-input"
                  />
                  <span>Yes, they do</span>
                </label>

                <label className={`radio-pill ${formData.productInfo.supplierDataAvailable === false ? "radio-pill--selected" : ""}`}>
                  <input
                    type="radio"
                    name="supplierDataAvailable"
                    value="no"
                    checked={formData.productInfo.supplierDataAvailable === false}
                    onChange={() => updateFormData("productInfo", "supplierDataAvailable", false)}
                    className="radio-input"
                  />
                  <span>No, they don't</span>
                </label>
              </div>
            </div>

            {formData.productInfo.supplierDataAvailable && (
              <div className="nested-section">
                <div className="form-field">
                  <label className="field-label">
                    Product-Level Emissions (kg CO₂e per unit)
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    className={`field-input ${hasError("supplier emissions") ? "field-input--error" : ""} ${isFilled(formData.productInfo.supplierEmissions) ? "field-input--filled" : ""}`}
                    placeholder="e.g., 2.5"
                    value={formData.productInfo.supplierEmissions}
                    onChange={(e) => updateFormData("productInfo", "supplierEmissions", e.target.value)}
                  />
                  {hasError("supplier emissions") && (
                    <div className="field-error">Supplier emissions value is required</div>
                  )}
                </div>

                <div className="form-field">
                  <label className="field-label">
                    Upload Documentation
                    <span className="field-required">*</span>
                  </label>
                  <div className={`file-upload ${hasError("documentation") || hasError("file") ? "file-upload--error" : ""}`}>
                    <input
                      type="file"
                      id="emissionsFile"
                      className="file-input"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                      disabled={uploadingFile}
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (file) {
                          handleFileUpload(file);
                        }
                      }}
                    />
                    <label htmlFor="emissionsFile" className="file-label">
                      {uploadingFile ? (
                        <>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            border: '2px solid #e5e7eb',
                            borderTopColor: '#06a457',
                            borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite'
                          }}></div>
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 4V12M10 4L7 7M10 4L13 7M4 12V16H16V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>
                            {formData.productInfo.supplierEmissionsFileName || "Choose file or drag here"}
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                  {uploadError && (
                    <div className="field-error">Upload failed: {uploadError}</div>
                  )}
                  {formData.productInfo.supplierEmissionsFileName && !uploadingFile && !uploadError && !hasError("documentation") && !hasError("file") && (
                    <div className="field-hint">
                      ✓ {formData.productInfo.supplierEmissionsFileName}
                      {formData.productInfo.supplierEmissionsFileUrl && (
                        <a
                          href={emissionsDocHref(formData.productInfo.supplierEmissionsFileUrl)}
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ marginLeft: '8px', color: '#06a457', textDecoration: 'underline' }}
                        >
                          View
                        </a>
                      )}
                    </div>
                  )}
                  {(hasError("documentation") || hasError("file")) && !uploadError && (
                    <div className="field-error">Documentation file is required</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Step1ProductInfo;


